import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import Fuse from 'fuse.js';
import useDeviceGroupByDeviceId from 'hooks/useDeviceGroupByDeviceId';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useDebounce } from 'use-debounce';

import Button from 'components/Button';
import CustomSearchbar from 'components/CustomSearchBar';
import DeviceList from 'components/DeviceComponents/DeviceList';
import GroupNames from 'components/DeviceComponents/GroupNames';
import RightComponentWrapper from 'components/DeviceComponents/RightComponentWrapper';
import ErrorText from 'components/ErrorText';
import DeviceGroupsIcon from 'components/Icons/DeviceGroupsIcon';

import { useAppDispatch, useAppSelector } from 'redux-store';
import addCurrentDevicesToOrganizationThunk from 'redux-store/thunks/organizationOverview/addCurrentDevicesToOrganizationThunk';
import getOrganizationByIdThunk from 'redux-store/thunks/organizations/getOrganizationByIdThunk';

import type { AddDevicesToOrganizationModalStageProps } from '..';

export const DeviceGroupData: FC<{ deviceId: number; serial: string }> = ({
  deviceId,
  serial,
}) => {
  const theme = useTheme();
  const deviceGroups = useDeviceGroupByDeviceId(deviceId);

  if (!deviceGroups || deviceGroups.length === 0) return null;

  return (
    <RightComponentWrapper testId={`device-${serial}-groups`}>
      <DeviceGroupsIcon fontSize="small" sx={{ color: 'grey.500' }} />
      <Typography
        data-testid="device-group-names"
        variant="body1"
        noWrap
        color={theme.vars.palette.grey[500]}
      >
        <GroupNames items={deviceGroups} />
      </Typography>
    </RightComponentWrapper>
  );
};

const AddDevicesToOrganization: FC<AddDevicesToOrganizationModalStageProps> = ({
  state,
  setState,
  onCloseProxy,
  onClose,
  organizationId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  const deviceList = useAppSelector(s => s.devices.items);

  const organizationDevices = useAppSelector(
    s => s.organizations.selectedOrganization.organization?.devices,
  );

  const unAssignedDevices = useMemo(() => {
    if (!organizationDevices) {
      return deviceList;
    }

    return deviceList.filter(
      device =>
        !organizationDevices.some(orgDevice => orgDevice.id === device.id),
    );
  }, [deviceList, organizationDevices]);

  const fullStatus = useAppSelector(appState => appState.api.fullStatus);

  const devicesWithNames = useMemo(() => {
    return unAssignedDevices.map(device => ({
      ...device,
      name: fullStatus[device.serial]?.fna,
    }));
  }, [unAssignedDevices, fullStatus]);

  const fuse = useMemo(
    () =>
      new Fuse(devicesWithNames, {
        keys: ['name', 'serial'],
        threshold: 0.4,
      }),
    [devicesWithNames],
  );

  const handleSearch = useCallback((): void => {
    if (!searchString) {
      setSearchResults(null);

      return;
    }

    const results = fuse.search(searchString);

    setSearchResults(results.map(result => result.item.serial));
  }, [fuse, searchString]);

  const [debouncedSearch] = useDebounce(searchString, 500);

  useEffect(() => {
    if (debouncedSearch) {
      handleSearch();
    }
  }, [debouncedSearch, handleSearch]);

  const handleClearSearch = useCallback((): void => {
    setSearchString('');
    setSearchResults(null);
  }, []);

  const selectedDeviceIds = useMemo(() => state.devices, [state.devices]);

  const setSelectedDeviceIds = useCallback(
    (devices: number[]) => {
      setState(prev => ({ ...prev, devices }));
    },
    [setState],
  );

  useEffect(() => {
    const newAddedDevice = deviceList.find(
      device => device.serial === state.newDeviceSerial,
    );

    if (newAddedDevice?.id) {
      setState(prev => ({ ...prev, devices: [newAddedDevice.id] }));
    }
  }, [deviceList, setState, state.newDeviceSerial]);

  const filteredDevices = useMemo(
    () =>
      searchResults
        ? devicesWithNames.filter(device =>
            searchResults.includes(device.serial),
          )
        : devicesWithNames,
    [devicesWithNames, searchResults],
  );

  const handleAssignDevicesToOrganization = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!organizationId) return;

      try {
        setLoading(true);

        const result = await dispatch(
          addCurrentDevicesToOrganizationThunk({
            organizationId: organizationId,
            data: {
              devices: selectedDeviceIds,
            },
          }),
        ).unwrap();

        if (result.success) {
          await dispatch(
            getOrganizationByIdThunk({
              organizationId: organizationId,
            }),
          ).unwrap();
          setState({ devices: [], stage: 'success' });

          setTimeout(() => {
            setState({ devices: [], stage: 'devices' });
            onClose();
          }, 1250);
        } else {
          setError("Could't assign devices"); //  to be defined for each device
        }
      } catch (e) {
        Sentry.captureException(e, {
          tags: { page: 'AddCurrentDevicesToOrganizationModal' },
        });
      } finally {
        setLoading(false);
      }
    },
    [dispatch, onClose, organizationId, selectedDeviceIds, setState],
  );

  return (
    <>
      <Box
        sx={{
          height: devicesWithNames.length > 0 ? '45vh' : 'auto',
          overflow: 'auto',
          paddingBottom: 5,
          width: '100%',
        }}
      >
        <Box mb={4}>
          <Typography
            variant="h2"
            mb={2}
            textAlign="center"
            data-testid="add-device-to-organisation-title"
          >
            {t('common:pages.organization_overview.add_devices_to_org.title')}
          </Typography>
        </Box>
        <Box mb={4} width="100%">
          <ListItemButton
            sx={{
              backgroundColor: theme.vars.palette.grey[100],
              borderRadius: 2,
            }}
            onClick={() => setState(prev => ({ ...prev, stage: 'addDevice' }))}
            data-testid="add-device-button"
          >
            <ListItemText
              color="primary"
              primaryTypographyProps={{
                style: {
                  color: theme.vars.palette.primary.dark,
                  fontWeight: 500,
                },
              }}
              primary={t('common:add_device')}
            />
            <IconButton edge="end" disableRipple>
              <AddCircleOutlineRoundedIcon color="primary" />
            </IconButton>
          </ListItemButton>
        </Box>
        <Box>
          {devicesWithNames.length > 0 ? (
            <Box>
              <Typography variant="h3" mb={1}>
                {t(
                  'common:pages.organization_overview.add_user.available_devices',
                )}
              </Typography>
              <Box mb={3} width="100%">
                <CustomSearchbar
                  search={searchString}
                  setSearch={setSearchString}
                  hasSearched={searchString.length > 0}
                  handleSearch={handleSearch}
                  handleClearSearch={handleClearSearch}
                  testId="add-device-search"
                />
              </Box>
              {filteredDevices.length > 0 ? (
                filteredDevices.map(item => (
                  <DeviceList
                    key={item.id}
                    deviceId={item.id}
                    serial={item.serial}
                    selectable
                    selectedDeviceIds={selectedDeviceIds}
                    setSelectedDeviceIds={setSelectedDeviceIds}
                    rightComponent={
                      <DeviceGroupData
                        deviceId={item.id}
                        serial={item.serial}
                      />
                    }
                  />
                ))
              ) : (
                <Typography color="grey.600" textAlign="center">
                  {t(
                    'common:pages.organization_overview.add_devices_to_org.no_devices_found',
                  )}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography color="grey.600" textAlign="center">
              {t(
                'common:pages.organization_overview.add_devices_to_org.no_devices_found',
              )}
            </Typography>
          )}
          <ErrorText error={error} />
        </Box>
      </Box>
      <Box
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        mt={2}
        gap={2}
      >
        <Button
          variant="flat"
          color="neutral"
          size="large"
          fullWidth
          onClick={onCloseProxy}
          data-testid="modal-cancel-button"
        >
          {t('common:cancel')}
        </Button>
        <Button
          variant="flat"
          size="large"
          fullWidth
          disabled={isLoading || selectedDeviceIds.length === 0}
          onClick={handleAssignDevicesToOrganization}
          data-testid="modal-save-button"
        >
          {t('common:save')}
        </Button>
      </Box>
    </>
  );
};

export default AddDevicesToOrganization;
